import * as API from '@aws-amplify/api'
import * as Storage from '@aws-amplify/storage'
import {isResolved, urlEncode, runWhile, retry, SimpleApi} from '@oc/utils'

const gatewayApiName = 'oc-permissions'

export enum ObjectType {
    Account = 'account',
    Region = 'region',
    Group = 'group',
    Role = 'role'
}

export interface AdmissableObject {
    name: string
    type: ObjectType
}

export interface Permission {
    action: string
    resource: string
    context: string | null
    name: string
    description: string
}

export interface Role {
    name: string
    context: ObjectType | null
    permissions: Permission[]
}

export interface UserAdmission {
    objectType: ObjectType | null
    objectName: string | null
    action: string
    resource: string
}

export interface RawAdmission {
    objectType: ObjectType | null
    objectName: string | null
    roleName: string
    groupName: string
}

export interface UserAdmissionResponse {
    body: UserAdmission[] | RawAdmission[]
    headers: {[key: string]: string}
}

export interface User {
    givenName: string
    familyName: string
    username: string
    email: string
    phoneNumber: string | null
    picture: string | null
    pictureUrl?: string
    lastLoggedInAt?: number
    groups?: string[]
    [key: string]: any
}

export interface GroupRequest {
    name: string
    description: string
    canBeEmpty: boolean
    picture: string | null
    pictureUrl?: string
}

export interface Group extends GroupRequest {
    canBeDeleted: boolean
    region?: string
    memberCount: number
    roleCount: number
    assignmentMeta?: AssignmentMetadata
}

export interface AssignmentMetadata {
    reason?: string
    actor?: string
    assignedAt?: string
}

export interface BackendUser {
    given_name: string
    family_name: string
    username: string
    email: string
    phone_number: string | null
    picture: string | null
    last_logged_in_at?: number
    groups?: string[]
    assignmentMetadata?: {
        reason?: string | null
        actor?: string | null
        assigned_at?: string | null
    }
}

export interface BackendUserGroup {
    name: string
    description: string
    picture: string | null
    can_be_empty: boolean
    can_be_deleted: boolean
    region?: string
    member_count: number
    role_count: number
    assignmentMetadata?: {
        reason?: string | null
        actor?: string | null
        assigned_at?: string | null
    }
}

export interface UsersResponse {
    readonly users: Array<BackendUser>
    readonly cursor: string | null
}

export class UserManagementService {
    public async getObjects(): Promise<AdmissableObject[]> {
        const path = urlEncode`/object`
        return SimpleApi.get(gatewayApiName, path)
    }

    public async getUser(username: string): Promise<User> {
        const path = urlEncode`/user/${username}`
        const user = await SimpleApi.get(gatewayApiName, path)
        return this.mapBackendUserToUser(user)
    }

    public async getUsers(): Promise<User[]> {
        const path = urlEncode`/user`
        const users: Array<BackendUser> = []
        let cursor: string | null = null

        await runWhile(async () => {
            const response: UsersResponse = await retry(async () => {
                if (cursor !== null) {
                    return await SimpleApi.get(gatewayApiName, path, {
                        queryParams: {
                            cursor
                        }
                    })
                }

                return await SimpleApi.get(gatewayApiName, path)
            })

            cursor = response.cursor

            users.push(...response.users)

            return cursor !== null
        }, 0)

        return users.map(this.mapBackendUserToUser)
    }

    public createUser(user: User): Promise<void> {
        const path = urlEncode`/user`
        return SimpleApi.post(gatewayApiName, path, {
            body: {
                ...this.mapUserToBackendUser(user)
            }
        })
    }

    public updateUser(user: User): Promise<boolean> {
        const path = urlEncode`/user/${user.username}`
        return isResolved(
            SimpleApi.put(gatewayApiName, path, {
                body: {
                    ...this.mapUserToBackendUser(user)
                }
            })
        )
    }

    public deleteUser(username: string): Promise<boolean> {
        const path = urlEncode`/user/${username}`
        return isResolved(SimpleApi.del(gatewayApiName, path, {body: {}}))
    }

    public forceSignOutUser(username: string): Promise<boolean> {
        const path: string = urlEncode`/user/${username}/signout`
        return isResolved(SimpleApi.post(gatewayApiName, path, {body: {}}))
    }

    public async getGroups(): Promise<Group[]> {
        const path = urlEncode`/group`
        const groups = await SimpleApi.get(gatewayApiName, path)
        return groups.map(this.mapBackendUserGroupToUserGroup)
    }

    public createGroup(group: GroupRequest): Promise<boolean> {
        const path = urlEncode`/group`
        return isResolved(
            SimpleApi.post(gatewayApiName, path, {
                body: {
                    name: group.name,
                    description: group.description,
                    can_be_empty: group.canBeEmpty,
                    picture: group.picture
                }
            })
        )
    }

    public updateGroup(group: GroupRequest): Promise<boolean> {
        const path = urlEncode`/group/${group.name}`
        return isResolved(
            SimpleApi.put(gatewayApiName, path, {
                body: {
                    name: group.name,
                    description: group.description,
                    can_be_empty: group.canBeEmpty,
                    picture: group.picture
                }
            })
        )
    }

    public deleteGroup(groupname: string): Promise<boolean> {
        const path = urlEncode`/group/${groupname}`
        return isResolved(SimpleApi.del(gatewayApiName, path, {body: {}}))
    }

    public async getUserGroups(userId: string): Promise<Group[]> {
        const path = urlEncode`/user/${userId}/group`
        const groups = await SimpleApi.get(gatewayApiName, path)
        return groups.map(this.mapBackendUserGroupToUserGroup)
    }

    public assignGroupToUser(username: string, group: string, reason: string): Promise<boolean> {
        const path = urlEncode`/user/${username}/group/${group}`
        return isResolved(SimpleApi.post(gatewayApiName, path, {body: {reason}}))
    }

    public unassignUserFromGroup(username: string, group: string): Promise<boolean> {
        const path = urlEncode`/user/${username}/group/${group}`
        return isResolved(SimpleApi.del(gatewayApiName, path, {body: {}}))
    }

    public async getGroupUsers(group: string): Promise<User[]> {
        const path = urlEncode`/group/${group}/users`
        const users = await SimpleApi.get(gatewayApiName, path)
        return users.map(this.mapBackendUserToUser)
    }
    private async fetchAdmissionsChunk(page: number, pageSize: number, isRaw: boolean): Promise<UserAdmissionResponse> {
        const path = urlEncode`/user/admissions`
        const response = await API.get({
            apiName: gatewayApiName,
            path,
            options: {
                queryParams: {
                    raw: `${isRaw}`,
                    page: `${page}`,
                    pageSize: `${pageSize}`
                }
            }
        }).response

        const headers = Object.fromEntries(
            Object.entries(response.headers).map(([key, value]) => [key.toLowerCase(), value as string])
        )

        const admissions = (await response.body.json()) as any[]

        return {
            body: isRaw
                ? admissions.map(a => ({
                      objectType: a.object_type,
                      objectName: a.object_name,
                      roleName: a.role_name,
                      groupName: a.group_name
                  }))
                : admissions.map(a => ({
                      objectType: a.object_type,
                      objectName: a.object_name,
                      action: a.action,
                      resource: a.resource
                  })),
            headers
        }
    }

    private async fetchAllAdmissions(isRaw: boolean): Promise<UserAdmission[] | RawAdmission[]> {
        const pageSize = 10000

        const initialChunk = await this.fetchAdmissionsChunk(0, pageSize, isRaw)

        if (initialChunk.headers['x-total-count']) {
            const totalCount = Number(initialChunk.headers['x-total-count'])
            const totalPages = Math.ceil(totalCount / pageSize)

            if (totalPages > 1) {
                const pagesToFetch = Array.from({length: totalPages - 1}, (_, index) => index + 1)
                const restOfPages = await Promise.all(
                    pagesToFetch.map(pageNum => this.fetchAdmissionsChunk(pageNum, pageSize, isRaw))
                )

                if (isRaw) {
                    return [initialChunk, ...restOfPages].flatMap(chunk => chunk.body as RawAdmission[])
                } else {
                    return [initialChunk, ...restOfPages].flatMap(chunk => chunk.body as UserAdmission[])
                }
            }
        }

        return initialChunk.body as UserAdmission[] | RawAdmission[]
    }

    public async getUserAdmissions(): Promise<UserAdmission[]> {
        return (await this.fetchAllAdmissions(false)) as UserAdmission[]
    }

    public async getUserAdmissionsRaw(): Promise<RawAdmission[]> {
        return (await this.fetchAllAdmissions(true)) as RawAdmission[]
    }

    public async getGroupAdmissions(group: string): Promise<RawAdmission[]> {
        const path = urlEncode`/group/${group}/admissions`
        const admissions = await SimpleApi.get(gatewayApiName, path)
        return await admissions.map((a: any) => ({
            objectType: a.object_type,
            objectName: a.object_name,
            roleName: a.role_name,
            groupName: group
        }))
    }

    public createAdmission(admission: RawAdmission): Promise<boolean> {
        const path = urlEncode`/admission`
        return isResolved(
            SimpleApi.post(gatewayApiName, path, {
                body: {
                    group_name: admission.groupName,
                    role_name: admission.roleName,
                    object_type: admission.objectType,
                    object_name: admission.objectName
                }
            })
        )
    }

    public deleteAdmission(admission: RawAdmission): Promise<boolean> {
        const path = urlEncode`/admission`
        return SimpleApi.del(gatewayApiName, path, {
            body: {
                group_name: admission.groupName,
                role_name: admission.roleName,
                object_type: admission.objectType,
                object_name: admission.objectName
            }
        })
    }

    public async getRoles(): Promise<Role[]> {
        const path = urlEncode`/role`
        return await SimpleApi.get(gatewayApiName, path)
    }

    public async uploadImage(fileName: string, file: File): Promise<void> {
        await Storage.uploadData({path: `public/${fileName}`, data: file}).result
    }

    private async getImageUrl(fileName: string): Promise<string | undefined> {
        return (await Storage.getUrl({path: `public/${fileName}`})).url.toString()
    }

    public async loadImages(users: User[], groups: Group[]): Promise<void> {
        const images = await this.getAllImages()

        await Promise.all([
            ...users.map(u => this.loadUserImage(u, images)),
            ...groups.map(u => this.loadGroupImage(u, images))
        ])
    }

    public async loadImageForUser(user: User): Promise<void> {
        if (user.picture) {
            user.pictureUrl = await this.getImageUrl(user.picture)
        }
    }

    private async getAllImages(): Promise<string[]> {
        try {
            const images = (
                await Storage.list({
                    path: 'public/images/',
                    options: {
                        listAll: true
                    }
                })
            ).items
            return images
                .filter(i => i.path.replace('public/images/', '') !== '')
                .map(i => i.path.replace('public/', ''))
        } catch (err) {
            return []
        }
    }

    private mapUserToBackendUser(user: User): BackendUser {
        return {
            email: user.email,
            phone_number: user.phoneNumber?.trim() || '',
            given_name: user.givenName,
            family_name: user.familyName,
            username: user.username,
            picture: user.picture
        }
    }

    private async loadUserImage(user: User, images: string[]) {
        if (user.picture && images.includes(user.picture)) {
            user.pictureUrl = await this.getImageUrl(user.picture)
        }
    }

    private async loadGroupImage(group: Group, images: string[]) {
        if (group.picture && images.includes(group.picture)) {
            group.pictureUrl = await this.getImageUrl(group.picture)
        }
    }

    private mapBackendUserToUser(user: BackendUser): User {
        const apiAssignmentMetadata = user.assignmentMetadata
        return {
            email: user.email,
            phoneNumber: user.phone_number,
            givenName: user.given_name,
            familyName: user.family_name,
            username: user.username,
            picture: user.picture,
            groups: user.groups,
            lastLoggedInAt: user.last_logged_in_at,
            assignmentMeta: apiAssignmentMetadata
                ? {
                      reason: apiAssignmentMetadata.reason ?? undefined,
                      actor: apiAssignmentMetadata.actor ?? undefined,
                      assignedAt: apiAssignmentMetadata.assigned_at ?? undefined
                  }
                : undefined
        }
    }

    private mapBackendUserGroupToUserGroup(group: BackendUserGroup): Group {
        const apiAssignmentMetadata = group.assignmentMetadata
        return {
            name: group.name,
            description: group.description,
            memberCount: group.member_count,
            roleCount: group.role_count,
            canBeEmpty: group.can_be_empty,
            canBeDeleted: group.can_be_deleted,
            region: group.region,
            picture: group.picture,
            assignmentMeta: apiAssignmentMetadata
                ? {
                      reason: apiAssignmentMetadata.reason ?? undefined,
                      actor: apiAssignmentMetadata.actor ?? undefined,
                      assignedAt: apiAssignmentMetadata.assigned_at ?? undefined
                  }
                : undefined
        }
    }
}

export default UserManagementService
